/**
 * RC10i · Shared expiration-status helper.
 *
 * Used by the Drivers roster (CDL / Medical / Passport columns) and
 * any future screen that renders a credential expiration badge. One
 * helper, three fields — identical color + text behavior.
 *
 * Rules (compared in CALENDAR DAYS against the configured display
 * timezone, NOT elapsed-hours arithmetic — avoids DST drift):
 *   - expiration > today + 30 days → GREEN / "Current"
 *   - expiration ∈ [today, today + 30 days] inclusive → YELLOW
 *       - "Expires today" at day 0
 *       - "Expires in N days" otherwise
 *   - expiration < today → RED / "Expired"
 *   - null / undefined → GRAY / "Not entered"
 *   - malformed string ("", "N/A", "0000-00-00", "not-a-date") → GRAY-ERROR / "Invalid date"
 *   - server flagged `restricted` → GRAY / "Restricted"
 *
 * Each badge MUST render both text AND color (accessibility — never
 * color alone). Caller is responsible for pairing the returned
 * `displayDate` + `label` + `color` into the DOM.
 */

import { differenceInCalendarDays, format, parseISO, isValid } from "date-fns";
import { toZonedTime, fromZonedTime } from "date-fns-tz";

// Default: America/Chicago per RC3. Overrideable via explicit arg so
// unit tests can pin a timezone. Production will inherit this default.
export const DEFAULT_DISPLAY_TZ = "America/Chicago";

const STATUS = Object.freeze({
  CURRENT: "current",
  WARNING: "warning",
  EXPIRED: "expired",
  MISSING: "missing",
  INVALID: "invalid",
  RESTRICTED: "restricted",
});

const COLOR = Object.freeze({
  GREEN: "green",
  YELLOW: "yellow",
  RED: "red",
  GRAY: "gray",
  GRAY_ERROR: "gray-error",
});

export const EXPIRATION_STATUS = STATUS;
export const EXPIRATION_COLOR = COLOR;

/**
 * Parse an incoming expiration value (ISO date, Date, or junk) into a
 * JS Date at local-midnight of the display timezone, or null.
 * Never throws.
 *
 * A date-only string "YYYY-MM-DD" is interpreted as that civil day at
 * midnight IN THE DISPLAY TIMEZONE (not UTC) so the badge for
 * "2026-05-15" reads as May 15 everywhere regardless of browser TZ.
 */
function _parse(value, displayTz) {
  if (value === null || value === undefined || value === "") return { parsed: null, invalid: false };
  if (value instanceof Date) {
    return isValid(value) ? { parsed: value, invalid: false } : { parsed: null, invalid: true };
  }
  if (typeof value !== "string") return { parsed: null, invalid: true };
  const trimmed = value.trim();
  if (!trimmed) return { parsed: null, invalid: false };
  // Known legacy junk markers → invalid, not missing
  const junk = ["n/a", "na", "none", "null", "0000-00-00"];
  if (junk.includes(trimmed.toLowerCase())) return { parsed: null, invalid: true };
  // Date-only "YYYY-MM-DD" → civil-date midnight in display tz
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/;
  const m = trimmed.match(dateOnly);
  if (m) {
    const [, y, mo, d] = m;
    const yy = Number(y), mm = Number(mo), dd = Number(d);
    if (mm < 1 || mm > 12 || dd < 1 || dd > 31) return { parsed: null, invalid: true };
    // Build a tz-local midnight Date (UTC instant that corresponds to
    // 00:00 in displayTz on that civil date).
    const iso = `${y}-${mo}-${d}T00:00:00`;
    const asUtc = fromZonedTime(iso, displayTz);
    if (!isValid(asUtc)) return { parsed: null, invalid: true };
    return { parsed: asUtc, invalid: false };
  }
  try {
    // Non-date-only input (full timestamp). Trust the embedded offset
    // / Z. parseISO handles both.
    const d = parseISO(trimmed);
    if (!isValid(d)) return { parsed: null, invalid: true };
    return { parsed: d, invalid: false };
  } catch {
    return { parsed: null, invalid: true };
  }
}

/**
 * Compute a credential-expiration badge descriptor.
 *
 * @param {string|Date|null|undefined} value - raw expiration input
 * @param {Object} [opts]
 * @param {string} [opts.displayTz=DEFAULT_DISPLAY_TZ]
 * @param {Date}   [opts.now] - fake-clock override for tests
 * @param {string} [opts.accessState] - server-side signal: "restricted"
 *                                      forces the Restricted badge path.
 * @returns {{status:string,color:string,label:string,displayDate:string|null}}
 */
export function expirationStatus(value, opts = {}) {
  const { displayTz = DEFAULT_DISPLAY_TZ, now, accessState } = opts;

  if (accessState === "restricted") {
    return {
      status: STATUS.RESTRICTED,
      color: COLOR.GRAY,
      label: "Restricted",
      displayDate: null,
    };
  }

  const { parsed, invalid } = _parse(value, displayTz);
  if (invalid) {
    return {
      status: STATUS.INVALID,
      color: COLOR.GRAY_ERROR,
      label: "Invalid date",
      displayDate: null,
    };
  }
  if (parsed === null) {
    return {
      status: STATUS.MISSING,
      color: COLOR.GRAY,
      label: "Not entered",
      displayDate: null,
    };
  }

  // Convert both expiration AND "today" to the display timezone, then
  // use `differenceInCalendarDays` which compares civil-calendar days
  // (not elapsed seconds — this is the DST-safe path).
  const nowSource = now instanceof Date ? now : new Date();
  const zonedToday = toZonedTime(nowSource, displayTz);
  const zonedExp = toZonedTime(parsed, displayTz);
  const diff = differenceInCalendarDays(zonedExp, zonedToday);

  // Format using the parsed date (not zonedExp) so day-only inputs
  // like "2027-01-15" render as "Jan 15, 2027" regardless of browser TZ.
  const displayDate = format(zonedExp, "MMM d, yyyy");

  if (diff < 0) {
    return { status: STATUS.EXPIRED, color: COLOR.RED, label: "Expired", displayDate };
  }
  if (diff === 0) {
    return { status: STATUS.WARNING, color: COLOR.YELLOW, label: "Expires today", displayDate };
  }
  if (diff <= 30) {
    const unit = diff === 1 ? "day" : "days";
    return {
      status: STATUS.WARNING,
      color: COLOR.YELLOW,
      label: `Expires in ${diff} ${unit}`,
      displayDate,
    };
  }
  return { status: STATUS.CURRENT, color: COLOR.GREEN, label: "Current", displayDate };
}

// Color → design-token mapping consumed by `<ExpirationBadge/>`.
export const EXP_COLOR_TOKENS = Object.freeze({
  green: {
    fg: "#1F6F3E",
    bg: "color-mix(in srgb, var(--brand-lime) 14%, transparent)",
    border: "color-mix(in srgb, var(--brand-lime) 36%, transparent)",
  },
  yellow: {
    fg: "#8A5A00",
    bg: "color-mix(in srgb, var(--status-hold) 16%, transparent)",
    border: "color-mix(in srgb, var(--status-hold) 42%, transparent)",
  },
  red: {
    fg: "#8A1F1F",
    bg: "color-mix(in srgb, var(--status-critical) 14%, transparent)",
    border: "color-mix(in srgb, var(--status-critical) 36%, transparent)",
  },
  gray: {
    fg: "var(--fg-muted)",
    bg: "color-mix(in srgb, var(--fg-muted) 10%, transparent)",
    border: "color-mix(in srgb, var(--fg-muted) 24%, transparent)",
  },
  "gray-error": {
    fg: "#5A3E00",
    bg: "color-mix(in srgb, #D79A27 12%, transparent)",
    border: "color-mix(in srgb, #D79A27 36%, transparent)",
  },
});
